import { apiClient } from './api'
import {
  SaleShop,
  CreateSaleShopData,
  UpdateSaleShopData,
  APIKey,
  CreateAPIKeyData,
  RegenerateAPIKeyData,
  Currency,
} from '@/types'

class SaleShopService {
  // Sale Shop CRUD operations
  async getSaleShops(params?: {
    page?: number
    page_size?: number
    search?: string
    is_active?: boolean
    currency?: number
    status?: string
    type?: string
  }): Promise<{
    success: boolean
    message: string
    data: {
      results: SaleShop[]
      count: number
      next: string | null
      previous: string | null
    }
  }> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.is_active !== undefined)
      queryParams.append('is_active', params.is_active.toString())
    if (params?.currency) queryParams.append('currency', params.currency.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.type) queryParams.append('type', params.type)

    const queryString = queryParams.toString()
    const url = queryString ? `/sale-shops/?${queryString}` : '/sale-shops/'

    return apiClient.get(url)
  }

  async getSaleShop(saleShopId: number): Promise<{
    success: boolean
    message: string
    data: SaleShop
  }> {
    return apiClient.get(`/sale-shops/${saleShopId}/`)
  }

  async createSaleShop(data: CreateSaleShopData): Promise<{
    success: boolean
    message: string
    data: SaleShop
  }> {
    return apiClient.post('/sale-shops/create/', data)
  }

  async updateSaleShop(
    saleShopId: number,
    data: UpdateSaleShopData
  ): Promise<{
    success: boolean
    message: string
    data: SaleShop
  }> {
    return apiClient.patch(`/sale-shops/${saleShopId}/`, data)
  }

  async deleteSaleShop(saleShopId: number): Promise<{
    success: boolean
    message: string
  }> {
    return apiClient.delete(`/sale-shops/${saleShopId}/`)
  }

  // Admin operations
  async getAdminSaleShops(params?: {
    page?: number
    page_size?: number
    search?: string
    owner?: number
    is_active?: boolean
    role?: string
  }): Promise<{
    success: boolean
    message: string
    data: {
      results: SaleShop[]
      count: number
      next: string | null
      previous: string | null
    }
  }> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.owner) queryParams.append('owner', params.owner.toString())
    if (params?.is_active !== undefined)
      queryParams.append('is_active', params.is_active.toString())
    if (params?.role) queryParams.append('role', params.role)

    const queryString = queryParams.toString()
    const url = queryString ? `/sale-shops/admin/all/?${queryString}` : '/sale-shops/admin/all/'

    return apiClient.get(url)
  }

  async createSaleShopForUser(data: CreateSaleShopData & { owner_id: number }): Promise<{
    success: boolean
    message: string
    data: SaleShop
  }> {
    return apiClient.post('/sale-shops/admin/create/', data)
  }

  // API Key operations
  async getAPIKeys(saleShopId: number): Promise<{
    success: boolean
    message: string
    data: APIKey[]
  }> {
    return apiClient.get(`/sale-shops/${saleShopId}/api-keys/`)
  }

  async getAPIKey(apiKeyId: number): Promise<{
    success: boolean
    message: string
    data: APIKey
  }> {
    return apiClient.get(`/sale-shops/api-keys/${apiKeyId}/`)
  }

  async createAPIKey(
    saleShopId: number,
    data: CreateAPIKeyData
  ): Promise<{
    success: boolean
    message: string
    data: APIKey
  }> {
    return apiClient.post(`/sale-shops/${saleShopId}/api-keys/create/`, data)
  }

  async regenerateAPIKey(
    saleShopId: number,
    data: RegenerateAPIKeyData
  ): Promise<{
    success: boolean
    message: string
    data: APIKey
  }> {
    return apiClient.post(`/sale-shops/${saleShopId}/api-keys/regenerate/`, data)
  }

  async deleteAPIKey(apiKeyId: number): Promise<{
    success: boolean
    message: string
  }> {
    return apiClient.delete(`/sale-shops/api-keys/${apiKeyId}/`)
  }

  // Utility methods
  async getCurrencies(): Promise<Currency[]> {
    return apiClient.get('/currencies/')
  }

  // Error handling
  handleError(error: any): string {
    if (error.response?.data?.message) {
      return error.response.data.message
    }
    if (error.response?.data?.errors) {
      return Array.isArray(error.response.data.errors)
        ? error.response.data.errors.join(', ')
        : error.response.data.errors
    }
    if (error.message) {
      return error.message
    }
    return 'An unexpected error occurred'
  }

  // Validation helpers
  validateSaleShopData(data: CreateSaleShopData | UpdateSaleShopData): string[] {
    const errors: string[] = []

    if ('name' in data && data.name && data.name.trim().length === 0) {
      errors.push('Sale shop name cannot be empty')
    }

    if (data.website_url && !this.isValidUrl(data.website_url)) {
      errors.push('Website URL must be a valid URL starting with http:// or https://')
    }

    if (data.webhook_url && !this.isValidUrl(data.webhook_url)) {
      errors.push('Webhook URL must be a valid URL starting with http:// or https://')
    }

    if (data.redirect_url && !this.isValidUrl(data.redirect_url)) {
      errors.push('Redirect URL must be a valid URL starting with http:// or https://')
    }

    if (data.cancel_url && !this.isValidUrl(data.cancel_url)) {
      errors.push('Cancel URL must be a valid URL starting with http:// or https://')
    }

    return errors
  }

  private isValidUrl(url: string): boolean {
    try {
      const urlObj = new URL(url)
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
    } catch {
      return false
    }
  }
}

export const saleShopService = new SaleShopService()
